import React from 'react'
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import {Bounce, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {IApplicationFormData, normalizeApplicationFormData} from '../../models/application-form'
import '../../style/application.scss'
import WizardHeader from './WizardHeader'
import {useWizard} from './useWizard'
import SocialLinks from '../../../../../app/shared/components/SocialLinks'
import { IsInviteApplication } from './coApplicantInviteContext'
import HouseholdJoinWarningModal from './HouseholdJoinWarningModal'
import type {HouseholdJoinWarning} from '../../api'

interface StepProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  trigger?: UseFormTrigger<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const MAX_CONTENT_WIDTH = 560
const SAVE_INDICATOR_REFRESH_INTERVAL_MS = 5000
const WIZARD_SCROLL_OFFSET_PX = 12
const WIZARD_SCROLL_TARGET_SELECTORS: Record<string, string> = {
  'emergency-contact': '[data-wizard-scroll-target="emergency-contact"]',
  'first-missing-upload': '[data-wizard-scroll-target="first-missing-upload"]',
  'proof-of-benefits': '[data-wizard-scroll-target="proof-of-benefits"]',
  'proof-of-employment': '[data-wizard-scroll-target="proof-of-employment"]',
  'rental-ledger': '[data-wizard-scroll-target="rental-ledger"]',
}

type WizardStartPageProps = {
  isRenterProfileEdit?: boolean
}

type SaveIndicatorProps = {
  lastSaved: Date | null
}

const getSavedText = (lastSaved: Date | null, now = Date.now()) => {
  if (!lastSaved) {
    return 'Your application auto-saves as you go.'
  }

  const seconds = Math.max(0, Math.floor((now - lastSaved.getTime()) / 1000))
  if (seconds < 60) {
    return `Saved ${seconds} second${seconds === 1 ? '' : 's'} ago`
  }

  const minutes = Math.floor(seconds / 60)
  return `Saved ${minutes} minute${minutes === 1 ? '' : 's'} ago`
}

const SaveIndicator = ({lastSaved}: SaveIndicatorProps) => {
  const [now, setNow] = React.useState(() => Date.now())

  React.useEffect(() => {
    setNow(Date.now())

    const intervalId = window.setInterval(() => {
      setNow(Date.now())
    }, SAVE_INDICATOR_REFRESH_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [lastSaved])

  return (
    <div className='wizard-save-indicator mt-4'>
      <span className='wizard-save-dot' aria-hidden='true'></span>
      <span>{getSavedText(lastSaved, now)}</span>
    </div>
  )
}

export default function WizardStartPage({isRenterProfileEdit = false}: WizardStartPageProps) {
  const {
    form,
    next,
    prev,
    currentStep,
    stepIndex,
    totalSteps,
    isCurrentStepValid,
    isSubmittingStep,
    isHydratingFromSession,
    isHouseholdJoinPending,
    reloadHouseholdJoinProfile,
    lastSaved,
    triggerValidation,
    wizardScrollTarget,
  } = useWizard({isRenterProfileEdit})
  const [householdJoin, setHouseholdJoin] = React.useState<HouseholdJoinWarning | null>(null)
  const {
    register,
    control,
    setValue,
    watch,
    trigger,
    formState: {errors},
  } = form
  const formData = normalizeApplicationFormData(watch())
  const isInvited = IsInviteApplication(formData)
  const otpRequiresNewCode = watch('otpRequiresNewCode')
  const isVerifyStepBlocked = currentStep?.path === 'verify-mobile' && otpRequiresNewCode
  const currentStepPath = currentStep?.path

  React.useLayoutEffect(() => {
    if (
      !currentStepPath ||
      isHydratingFromSession ||
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof window.scrollTo !== 'function'
    ) {
      return
    }

    const targetSelector = wizardScrollTarget
      ? WIZARD_SCROLL_TARGET_SELECTORS[wizardScrollTarget]
      : undefined
    const targetElement = targetSelector
      ? document.querySelector<HTMLElement>(targetSelector)
      : null

    if (targetElement) {
      const headerHeight = document.querySelector<HTMLElement>('.wizard-header')?.offsetHeight ?? 0
      const targetTop =
        targetElement.getBoundingClientRect().top +
        window.pageYOffset -
        headerHeight -
        WIZARD_SCROLL_OFFSET_PX

      window.scrollTo({top: Math.max(0, targetTop), left: 0, behavior: 'auto'})
      return
    }

    window.scrollTo({top: 0, left: 0, behavior: 'auto'})
  }, [currentStepPath, isHydratingFromSession, wizardScrollTarget])
  const headerTitle = currentStep?.key === 'start' && isInvited ? 'Confirm Details' : currentStep?.title ?? ''
  const footerText =
    currentStep?.key === 'income' && isInvited
      ? 'Pick at least one income source.'
      : currentStep?.footerText

  if (isHydratingFromSession || !currentStep) {
    return <div>Loading step...</div>
  }

  const StepComponent = currentStep.component as React.ComponentType<StepProps>
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void next()
  }

  const errorMessages = Object.keys(errors).map((key) => (
    <div key={key} className='error'>
      {(errors as any)[key]?.message} {/* Typecast errors to 'any' to avoid TypeScript error */}
    </div>
  ))

  return (
    <>
      <div inert={isHouseholdJoinPending} aria-hidden={isHouseholdJoinPending || undefined}>
        <WizardHeader
          step={stepIndex}
          showProgressBar={!isRenterProfileEdit && !isHouseholdJoinPending && currentStep.showProgressBar}
          totalSteps={totalSteps}
          title={headerTitle}
          onBack={isHouseholdJoinPending ? undefined : prev}
          data={isHouseholdJoinPending ? {
            ...formData,
            primaryApplicantName: householdJoin && householdJoin.sessionId === formData.sessionId ? householdJoin.primaryRenterFirstName : undefined,
          } : formData}
        />

        <main className='wizard-page px-3 px-md-6 py-6 py-md-10'>
          <div className='mx-auto w-100' style={{maxWidth: MAX_CONTENT_WIDTH}}>
            <form onSubmit={handleFormSubmit} noValidate>
              <fieldset disabled={isHouseholdJoinPending} className='border-0 p-0 m-0'>
                <StepComponent
                  register={register}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  watch={watch}
                  trigger={trigger}
                  triggerValidation={isHouseholdJoinPending ? undefined : triggerValidation}
                />
                <div className='wizard-footer border-top mt-10'>
                  <button
                    type='submit'
                    className='btn wizard-primary-action w-100'
                    disabled={isHouseholdJoinPending || isSubmittingStep || !isCurrentStepValid || isVerifyStepBlocked}
                  >
                    {isSubmittingStep
                      ? 'Saving...'
                      : isRenterProfileEdit
                        ? 'Save changes'
                        : (currentStep.nextButtonText ?? 'Continue')}
                  </button>
                </div>
                {footerText && (
                  <span className='d-block text-muted text-center fs-5'>{footerText}</span>
                )}
              </fieldset>
            </form>
            {!isHouseholdJoinPending && currentStep.key !== 'verify-mobile' && !(isInvited && currentStep.key === 'start') && (
              <SaveIndicator lastSaved={lastSaved} />
            )}
          </div>
        </main>
      </div>
      {isHouseholdJoinPending && formData.sessionId && (
        <HouseholdJoinWarningModal
          key={formData.sessionId}
          sessionId={formData.sessionId}
          onCompleted={reloadHouseholdJoinProfile}
          onJourneyLoaded={setHouseholdJoin}
        />
      )}
      <ToastContainer
        position='top-right'
        autoClose={1500}
        transition={Bounce}
        pauseOnHover
        pauseOnFocusLoss
        closeOnClick
        draggable
        draggablePercent={80}
        draggableDirection='x'
        role='alert'
        theme='light'
      />
    </>
  )
}
